import type {
  AtlasPinCandidateMessage,
  AtlasViewerMode,
  ThreadStatus,
} from '@atlas/api-contract';
import { create } from 'zustand';

import {
  DEFAULT_WIDTH_PRESET_ID,
  presetForViewportWidth,
  type ViewerWidthPresetId,
} from './width-presets';

/**
 * Everything the viewer knows about itself that is not server data.
 *
 * ============================================================================
 * WHAT LIVES HERE, AND WHAT DELIBERATELY DOES NOT
 * ============================================================================
 * Three sources of truth meet in the viewer and keeping them apart is the whole
 * design of this store:
 *
 *  - **The URL** owns which version is open. `?version=<id>` is what a reviewer
 *    pastes into a message and what a reload has to survive, so the store only
 *    *mirrors* it — `openVersion` is called from the route once the id has been
 *    resolved, never the other way round.
 *  - **React Query** owns the prototype, the version list and the threads.
 *    None of it is copied in here; a second copy is a second thing to
 *    invalidate.
 *  - **This store** owns what the reviewer is doing: where they are, how wide
 *    they are looking, whether they are commenting, and what the side panel is
 *    showing. Section 20's thread panel reads every field below.
 *
 * ============================================================================
 * WHAT RESETS ON A VERSION SWITCH, AND WHY THE REST DOES NOT
 * ============================================================================
 * `openVersion` clears everything that *names something inside the old
 * version*: the screen path (that version's file list), the pending pin
 * (anchored to that version's DOM), the selected and highlighted threads
 * (threads never migrate between versions), and the panel filters. Left alone
 * they would not merely be stale, they would be wrong — a screen path pointing
 * at a file the new version does not contain, a thread id the new version's
 * thread list will never return.
 *
 * Width preset, comment mode and panel collapse are kept, because they describe
 * how the reviewer is working rather than what they are looking at. Someone
 * comparing v2 against v3 at Mobile width wants to still be at Mobile width
 * after the switch; resetting that would make the comparison — the entire point
 * of having versions — impossible to perform.
 */

/**
 * A comment-mode click that has not yet become a thread: the anchor the agent
 * captured plus where to float the composer. It is exactly the
 * `atlas:pin-candidate` message minus its discriminant, so Section 20 can drop
 * an inbound message straight in without restating a single field.
 */
export type PendingPin = Omit<AtlasPinCandidateMessage, 'type'>;

/** What the thread panel is filtered to (spec R5.6). */
export interface ViewerPanelFilters {
  /** `open` by default; "show resolved" widens it to `all`. */
  readonly status: ThreadStatus;
  /**
   * Restrict to threads pinned to the screen on display. Off by default: a
   * reviewer opening the panel wants to see the outstanding work on the whole
   * version, not only on whichever screen happens to be up.
   */
  readonly currentScreenOnly: boolean;
}

export const DEFAULT_PANEL_FILTERS: ViewerPanelFilters = {
  status: 'open',
  currentScreenOnly: false,
};

export interface ViewerState {
  /** Mirrors `?version=<id>`. `null` before it resolves, or with no versions. */
  versionId: string | null;
  /**
   * The screen on display, in the agent's spelling — root-relative, hash
   * included. `null` until the agent reports or the entry path seeds it.
   */
  screenPath: string | null;
  /**
   * In-document screen identity for single-URL client-side apps — the agent's
   * `screenKey` from `atlas:ready`/`atlas:navigated`. `null` for classic
   * URL-navigated prototypes and until the agent reports.
   */
  screenKey: string | null;
  widthPreset: ViewerWidthPresetId;
  mode: AtlasViewerMode;
  filters: ViewerPanelFilters;
  panelCollapsed: boolean;
  /** Whether the left rail of sibling-prototype shortcuts is collapsed. */
  railCollapsed: boolean;
  pendingPin: PendingPin | null;
  /** Thread whose popover is open (spec R3.6). */
  selectedThreadId: string | null;
  /** Thread under the cursor in the panel, emphasised in the frame. */
  highlightedThreadId: string | null;
  /**
   * Threads already asked about the viewport switch, accepted or declined.
   *
   * Declining is a decision about a thread rather than about a moment, so the
   * offer is made once and then never again — otherwise re-reading a long
   * thread would mean refusing the same prompt on every visit, and the only way
   * to silence it would be to accept an answer already refused (spec R3.6).
   */
  viewportOfferedThreadIds: readonly string[];

  /** Point the viewer at a version, resetting anything scoped to the old one. */
  openVersion: (versionId: string | null) => void;
  setScreenPath: (screenPath: string | null) => void;
  /** Adopt the in-document screen identity the agent reported (or `null`). */
  setScreenKey: (screenKey: string | null) => void;
  setWidthPreset: (widthPreset: ViewerWidthPresetId) => void;
  /** Adopt the width a thread was recorded at (spec R3.6). */
  matchViewportWidth: (viewportWidth: number) => void;
  setMode: (mode: AtlasViewerMode) => void;
  toggleMode: () => void;
  setFilters: (patch: Partial<ViewerPanelFilters>) => void;
  setPanelCollapsed: (panelCollapsed: boolean) => void;
  togglePanel: () => void;
  setRailCollapsed: (railCollapsed: boolean) => void;
  toggleRail: () => void;
  setPendingPin: (pendingPin: PendingPin | null) => void;
  selectThread: (threadId: string | null) => void;
  highlightThread: (threadId: string | null) => void;
  /** Record that the viewport switch has been offered for this thread. */
  markViewportOffered: (threadId: string) => void;
  /** Back to a freshly opened viewer. Called when the viewer unmounts. */
  reset: () => void;
}

/** State scoped to one version — everything `openVersion` throws away. */
const perVersionDefaults = {
  screenPath: null,
  screenKey: null,
  filters: DEFAULT_PANEL_FILTERS,
  pendingPin: null,
  selectedThreadId: null,
  highlightedThreadId: null,
  viewportOfferedThreadIds: [],
} satisfies Partial<ViewerState>;

/** State scoped to the viewer as a whole. */
const sessionDefaults = {
  versionId: null,
  widthPreset: DEFAULT_WIDTH_PRESET_ID,
  mode: 'browse',
  panelCollapsed: false,
  railCollapsed: false,
} satisfies Partial<ViewerState>;

export const useViewerStore = create<ViewerState>()((set) => ({
  ...sessionDefaults,
  ...perVersionDefaults,

  openVersion: (versionId) => {
    set((state) =>
      /*
       * Idempotent on purpose. The route calls this from an effect that reruns
       * whenever the version list refetches, and a reset on every refetch would
       * throw the reviewer back to the entry screen mid-review.
       */
      state.versionId === versionId
        ? state
        : { ...perVersionDefaults, versionId },
    );
  },

  setScreenPath: (screenPath) => {
    set((state) =>
      state.screenPath === screenPath
        ? state
        : {
            screenPath,
            /*
             * A pin candidate belongs to the screen it was clicked on. Leaving
             * it through a navigation would open the composer over a page the
             * click never touched.
             */
            pendingPin: null,
          },
    );
  },

  setScreenKey: (screenKey) => {
    set((state) => (state.screenKey === screenKey ? state : { screenKey }));
  },

  setWidthPreset: (widthPreset) => {
    set({ widthPreset });
  },

  matchViewportWidth: (viewportWidth) => {
    set({ widthPreset: presetForViewportWidth(viewportWidth) });
  },

  setMode: (mode) => {
    set((state) =>
      state.mode === mode
        ? state
        : {
            mode,
            // Leaving comment mode abandons an unposted pin: the composer is
            // gone, so keeping its anchor would strand invisible state.
            pendingPin: mode === 'comment' ? state.pendingPin : null,
          },
    );
  },

  toggleMode: () => {
    set((state) =>
      state.mode === 'comment'
        ? { mode: 'browse', pendingPin: null }
        : { mode: 'comment' },
    );
  },

  setFilters: (patch) => {
    set((state) => ({ filters: { ...state.filters, ...patch } }));
  },

  setPanelCollapsed: (panelCollapsed) => {
    set({ panelCollapsed });
  },

  togglePanel: () => {
    set((state) => ({ panelCollapsed: !state.panelCollapsed }));
  },

  setRailCollapsed: (railCollapsed) => {
    set({ railCollapsed });
  },

  toggleRail: () => {
    set((state) => ({ railCollapsed: !state.railCollapsed }));
  },

  setPendingPin: (pendingPin) => {
    /*
     * A new pin candidate and an open thread are two popovers competing for the
     * same corner of the same prototype. The click that produced the candidate
     * is the reviewer saying what they want to look at, so it wins.
     */
    set(pendingPin === null ? { pendingPin } : { pendingPin, selectedThreadId: null });
  },

  selectThread: (selectedThreadId) => {
    /* And the same rule in the other direction. */
    set(
      selectedThreadId === null
        ? { selectedThreadId }
        : { selectedThreadId, pendingPin: null },
    );
  },

  highlightThread: (highlightedThreadId) => {
    set({ highlightedThreadId });
  },

  markViewportOffered: (threadId) => {
    set((state) =>
      state.viewportOfferedThreadIds.includes(threadId)
        ? state
        : {
            viewportOfferedThreadIds: [
              ...state.viewportOfferedThreadIds,
              threadId,
            ],
          },
    );
  },

  reset: () => {
    set({ ...sessionDefaults, ...perVersionDefaults });
  },
}));
